/**
 * Weight is ALWAYS an integer number of grams in app state, outbox payloads and the local DB.
 * The UI shows and accepts kilograms with exactly 3 decimals (gram precision, as gate scales read).
 */

export type Grams = number

function assertGrams(value: Grams): void {
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`Weight must be an integer number of grams, got ${value}`)
  }
}

const GROUPING = new Intl.NumberFormat('en-IN', { useGrouping: true, maximumFractionDigits: 0 })

export interface FormatWeightOptions {
  /** Append " kg". Default true. */
  unit?: boolean
  /** Indian digit grouping on the whole-kg part (1,234.500). Default true. */
  grouping?: boolean
}

/** 12345 → "12.345 kg"; -500 → "−0.500 kg". Always 3 decimals. */
export function formatGrams(value: Grams, opts: FormatWeightOptions = {}): string {
  const { unit = true, grouping = true } = opts
  assertGrams(value)
  const negative = value < 0
  const abs = Math.abs(value)
  const kg = Math.trunc(abs / 1000)
  const g = abs % 1000
  const kgText = grouping ? GROUPING.format(kg) : String(kg)
  return `${negative ? '−' : ''}${kgText}.${String(g).padStart(3, '0')}${unit ? ' kg' : ''}`
}

/** Plain "12.345" for an <input> value (no grouping, no unit). */
export function gramsToKgInput(value: Grams): string {
  return formatGrams(value, { unit: false, grouping: false })
}

/**
 * Parse a kilogram reading typed on the numeric keypad into grams — string arithmetic, no floats.
 * Accepts "12", "12.", "12.3", "12.345", ".5", "12,345" (comma as decimal separator on some
 * keyboards). Returns null if invalid or more than 3 decimals.
 */
export function parseKgToGrams(input: string): Grams | null {
  const cleaned = input.trim().replace(',', '.').replace(/\s/g, '')
  const m = /^(-)?(\d*)(?:\.(\d{0,3}))?$/.exec(cleaned)
  if (!m) return null
  const [, sign, whole = '', frac = ''] = m
  if (whole === '' && frac === '') return null
  const grams = Number(whole || '0') * 1000 + Number(frac.padEnd(3, '0') || '0')
  if (!Number.isSafeInteger(grams)) return null
  return sign ? -grams : grams
}

/**
 * Sanitize keystrokes for a kg field: digits and one decimal point, max 3 decimals, max
 * `maxWholeDigits` before the point. Returns the cleaned string to put back in the input.
 */
export function sanitizeKgInput(raw: string, maxWholeDigits = 5): string {
  let s = raw.replace(',', '.').replace(/[^\d.]/g, '')
  const dot = s.indexOf('.')
  if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '')
  const [whole = '', frac] = s.split('.')
  const w = whole.replace(/^0+(?=\d)/, '').slice(0, maxWholeDigits)
  return frac === undefined ? w : `${w}.${frac.slice(0, 3)}`
}

/** |a − b| in grams — compare readings in whole grams (R21, R27). */
export function absDiffGrams(a: Grams, b: Grams): Grams {
  assertGrams(a)
  assertGrams(b)
  return Math.abs(a - b)
}
